import { useCallback, useEffect, useMemo, useState } from "react";
import {
  FiCalendar,
  FiCheckCircle,
  FiDollarSign,
  FiEye,
  FiTrash2,
  FiFileText,
  FiPlus,
  FiRefreshCw,
  FiUserCheck,
  FiUsers,
  FiX,
  FiChevronDown,
  FiChevronUp,
  FiClock,
  FiTrendingDown,
  FiTrendingUp,
} from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { apiRequest } from "../../services/api";
import { useAuth } from "../../context/AuthContext";
import { hasPermission, isSuperAdmin } from "../../utils/permissions";
import PayrollAdvanceRecoveryCard from "./components/PayrollAdvanceRecoveryCard";
import HrmsFilters from '../../components/hrms/HrmsFilterPanel';
import { matchesHrmsFilters } from '../../components/hrms/hrmsFilterUtils';

function numberValue(value) {
  return Number(value || 0);
}

function formatMoney(value) {
  return `₹${numberValue(value).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function formatMonth(year, month) {
  return new Intl.DateTimeFormat("en-IN", {
    month: "long",
    year: "numeric",
  }).format(new Date(year, month - 1, 1));
}

function formatDate(value) {
  if (!value) return "—";

  return new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(new Date(value));
}

function formatMinutes(value) {
  const total = Math.max(0, Math.round(numberValue(value)));

  if (!total) return "0 min";

  const hours = Math.floor(total / 60);
  const minutes = total % 60;

  if (!hours) return `${minutes} min`;
  if (!minutes) return `${hours}h`;

  return `${hours}h ${minutes}m`;
}

function formatTime(value) {
  if (!value) return "—";

  return new Intl.DateTimeFormat("en-IN", {
    timeZone: "Asia/Kolkata",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  }).format(new Date(value));
}

function SessionTimeline({ sessions = [] }) {
  if (!sessions.length)
    return <span className="text-gray-400">No sessions</span>;
  return (
    <div className="flex min-w-[220px] flex-wrap gap-1.5">
      {sessions.map((session, index) => (
        <span
          key={session.id || index}
          className="rounded-lg bg-gray-100 px-2 py-1 text-xs font-medium text-gray-700"
        >
          {formatTime(session.checkInAt)} - {formatTime(session.checkOutAt)}
        </span>
      ))}
    </div>
  );
}

function attendanceStatusStyle(status) {
  const styles = {
    PRESENT: "bg-emerald-50 text-emerald-700",
    LATE: "bg-amber-50 text-amber-700",
    HALF_DAY: "bg-orange-50 text-orange-700",
    ABSENT: "bg-red-50 text-red-700",
    ON_LEAVE: "bg-blue-50 text-blue-700",
    HOLIDAY: "bg-violet-50 text-violet-700",
    WEEKLY_OFF: "bg-violet-50 text-violet-700",
    ALREADY_SETTLED_OFF_CYCLE: "bg-gray-100 text-gray-600",
  };

  return styles[status] || "bg-gray-100 text-gray-600";
}

function attendanceStatusLabel(status) {
  return String(status || "—")
    .replaceAll("_", " ")
    .toLowerCase()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function statusStyle(status) {
  const styles = {
    DRAFT: "bg-yellow-100 text-yellow-700",
    POSTED: "bg-blue-100 text-blue-700",
    VOIDED: "bg-red-100 text-red-700",
    PAID: "bg-green-100 text-green-700",
  };

  return styles[status] || "bg-gray-100 text-gray-700";
}

export default function Payroll() {
  const navigate = useNavigate();
  const { accessToken, user } = useAuth();

  const [allRuns, setRuns] = useState([]);
  const [runFilters, setRunFilters] = useState({});
  const runs = useMemo(() => allRuns.filter(run => !Object.values(runFilters).some(Boolean) || (run.items || []).some(item => matchesHrmsFilters(item,runFilters))), [allRuns,runFilters]);
  const [selectedRun, setSelectedRun] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [error, setError] = useState("");
  const [postingRunId, setPostingRunId] = useState(null);
  const [deletingRunId, setDeletingRunId] = useState(null);

  const loadRuns = useCallback(async () => {
    if (!accessToken) return;

    try {
      setIsLoading(true);
      setError("");

      const response = await apiRequest("/payroll/runs", {
        accessToken,
      });

      setRuns(response || []);
    } catch (requestError) {
      setError(requestError.message || "Unable to load payroll runs.");
      setRuns([]);
    } finally {
      setIsLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    loadRuns();
  }, [loadRuns]);

  const summary = useMemo(() => {
    const latestRun = runs[0];

    return {
      totalRuns: runs.length,
      drafts: runs.filter((run) => run.status === "DRAFT").length,
      posted: runs.filter((run) => run.status === "POSTED").length,
      latestEmployees: latestRun?._count?.items || 0,
    };
  }, [runs]);

  const openRun = async (runId) => {
    if (!accessToken) return;

    try {
      setIsLoadingDetails(true);
      setError("");

      const response = await apiRequest(`/payroll/runs/${runId}`, {
        accessToken,
      });

      setSelectedRun(response);
    } catch (requestError) {
      setError(requestError.message || "Unable to load payroll details.");
    } finally {
      setIsLoadingDetails(false);
    }
  };

  const postRun = async (runId) => {
    if (!accessToken) return;

    const confirmed = window.confirm(
      "Post this payroll run? Its payroll values and adjustments will be locked.",
    );

    if (!confirmed) return;

    try {
      setPostingRunId(runId);
      setError("");

      await apiRequest(`/payroll/runs/${runId}/post`, {
        method: "POST",
        accessToken,
      });

      await loadRuns();

      if (selectedRun?.id === runId) {
        const refreshedRun = await apiRequest(`/payroll/runs/${runId}`, {
          accessToken,
        });

        setSelectedRun(refreshedRun);
      }
    } catch (requestError) {
      setError(requestError.message || "Unable to post payroll.");
    } finally {
      setPostingRunId(null);
    }
  };

  const deleteRun = async (run) => {
    if (!accessToken || !hasPermission(user, "payroll.delete")) return;
    if (!window.confirm(`Permanently delete ${formatMonth(run.periodYear, run.periodMonth)} payroll and all its payslips, adjustments and payment records? Payroll advance recoveries will be reversed. Actual bank transfers will NOT be refunded. Attendance and employees will remain unchanged.`)) return;
    const reason = window.prompt("Reason for deleting this payroll (5–500 characters):");
    if (reason == null) return;
    if (reason.trim().length < 5 || reason.length > 500) { setError("Enter a deletion reason of 5–500 characters."); return; }
    try {
      setDeletingRunId(run.id);
      setError("");
      await apiRequest(`/payroll/runs/${run.id}`, { method: "DELETE", accessToken, body: { confirmation: run.id, reason: reason.trim() } });
      if (selectedRun?.id === run.id) setSelectedRun(null);
      await loadRuns();
    } catch (requestError) { setError(requestError.message || "Unable to delete payroll."); }
    finally { setDeletingRunId(null); }
  };

  const markPaid = async (itemId) => {
    if (!accessToken || !selectedRun) return;

    const confirmed = window.confirm("Mark this employee payroll as paid?");

    if (!confirmed) return;

    try {
      await apiRequest(`/payroll/items/${itemId}/mark-paid`, {
        method: "POST",
        accessToken,
        body: {},
      });

      await openRun(selectedRun.id);
      await loadRuns();
    } catch (requestError) {
      setError(requestError.message || "Unable to mark payroll as paid.");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <p className="text-sm text-gray-500">HRMS / Payroll</p>

          <h1 className="mt-1 text-3xl font-bold text-gray-900">
            Payroll Runs
          </h1>

          <p className="mt-2 text-gray-500">
            Review draft payroll, post approved runs, and track employee
            payments.
          </p>
        </div>

        <div className="flex flex-wrap gap-3">
          <button
            onClick={loadRuns}
            disabled={isLoading}
            className="inline-flex items-center gap-2 rounded-xl border border-gray-200 px-4 py-3 font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            <FiRefreshCw className={isLoading ? "animate-spin" : ""} />
            Refresh
          </button>

          <button
            onClick={() => navigate("/admindashboard/payroll/off-cycle")}
            className="inline-flex items-center gap-2 rounded-xl border border-primary-200 bg-primary-50 px-5 py-3 font-medium text-primary-700 hover:bg-primary-100"
          >
            <FiUserCheck size={18} />
            Off-cycle Settlement
          </button>

          <button
            onClick={() => navigate("/admindashboard/payroll/generate")}
            className="inline-flex items-center gap-2 rounded-xl bg-primary-700 px-5 py-3 font-medium text-white hover:bg-primary-800"
          >
            <FiPlus size={18} />
            Generate Payroll
          </button>
        </div>
      </div>

      {error ? (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-5 py-4 text-sm text-red-700">
          {error}
        </div>
      ) : null}

      <HrmsFilters records={allRuns.flatMap(run => run.items || [])} value={runFilters} onChange={setRunFilters} />
      <p className="text-sm text-gray-500">Matching runs contain at least one selected employee. Run totals cover the complete payroll run; open a run to filter its employee details.</p>
      <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-4">
        <StatCard
          title="Payroll Runs"
          value={summary.totalRuns}
          icon={<FiFileText size={22} />}
          color="bg-primary-50 text-primary-700"
        />

        <StatCard
          title="Draft Runs"
          value={summary.drafts}
          icon={<FiClock size={22} />}
          color="bg-yellow-50 text-yellow-700"
        />

        <StatCard
          title="Posted Runs"
          value={summary.posted}
          icon={<FiCheckCircle size={22} />}
          color="bg-blue-50 text-blue-700"
        />

        <StatCard
          title="Latest Run Employees"
          value={summary.latestEmployees}
          icon={<FiUsers size={22} />}
          color="bg-green-50 text-green-700"
        />
      </div>

      <div className="overflow-hidden rounded-3xl border border-gray-100 bg-white shadow-sm">
        <div className="flex items-center justify-between border-b border-gray-100 p-6">
          <div>
            <h2 className="text-xl font-bold text-gray-900">
              All Payroll Runs
            </h2>

            <p className="mt-1 text-sm text-gray-500">
              Payroll is locked only after it has been posted.
            </p>
          </div>
        </div>

        {isLoading ? (
          <div className="py-20 text-center text-gray-500">
            Loading payroll runs...
          </div>
        ) : runs.length === 0 ? (
          <div className="flex flex-col items-center py-20 text-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-primary-50 text-primary-700">
              <FiDollarSign size={24} />
            </div>

            <h3 className="mt-5 text-lg font-semibold text-gray-900">
              No payroll runs yet
            </h3>

            <p className="mt-2 max-w-sm text-sm text-gray-500">
              Generate a monthly draft payroll to calculate attendance,
              deductions, overtime, and POS commission.
            </p>

            <button
              onClick={() => navigate("/admindashboard/payroll/generate")}
              className="mt-6 rounded-xl bg-primary-700 px-5 py-3 font-medium text-white hover:bg-primary-800"
            >
              Generate First Payroll
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[900px]">
              <thead className="bg-gray-50">
                <tr>
                  <TableHead>Payroll Period</TableHead>
                  <TableHead>Employees</TableHead>
                  <TableHead>Calculation Through</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Generated On</TableHead>
                  <TableHead align="right">Action</TableHead>
                </tr>
              </thead>

              <tbody>
                {runs.map((run) => (
                  <tr
                    key={run.id}
                    className="border-t border-gray-100 transition hover:bg-gray-50"
                  >
                    <td className="px-6 py-5">
                      <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary-50 text-primary-700">
                          <FiCalendar />
                        </div>

                        <div>
                          <p className="font-semibold text-gray-900">
                            {formatMonth(run.periodYear, run.periodMonth)}
                          </p>

                          <p className="mt-1 text-xs text-gray-500">
                            {formatDate(run.periodStart)} –{" "}
                            {formatDate(run.periodEnd)}
                          </p>
                        </div>
                      </div>
                    </td>

                    <td className="px-6 py-5 font-medium text-gray-900">
                      {run._count?.items || 0}
                    </td>

                    <td className="px-6 py-5 text-sm text-gray-600">
                      {formatDate(run.calculationThrough)}
                    </td>

                    <td className="px-6 py-5 text-sm text-gray-600">
                      <span className="inline-flex rounded-full bg-gray-100 px-3 py-1 text-xs font-semibold text-gray-700">
                        {run.runType === "OFF_CYCLE" ? "Off-cycle" : "Regular"}
                      </span>
                    </td>

                    <td className="px-6 py-5">
                      <span
                        className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${statusStyle(
                          run.status,
                        )}`}
                      >
                        {run.status}
                      </span>
                    </td>

                    <td className="px-6 py-5 text-sm text-gray-600">
                      {formatDate(run.generatedAt)}
                    </td>

                    <td className="px-6 py-5 text-right">
                      <div className="flex justify-end gap-2">
                        {run.status === "DRAFT" ? (
                          <button
                            onClick={() => postRun(run.id)}
                            disabled={postingRunId === run.id}
                            className="rounded-xl bg-primary-700 px-4 py-2 text-xs font-semibold text-white hover:bg-primary-800 disabled:opacity-50"
                          >
                            {postingRunId === run.id
                              ? "Posting..."
                              : "Post Payroll"}
                          </button>
                        ) : null}

                        <button
                          onClick={() => openRun(run.id)}
                          className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-gray-200 text-gray-600 hover:bg-primary-700 hover:text-white"
                          title="View payroll run"
                        >
                          <FiEye />
                        </button>
                        {hasPermission(user, "payroll.delete") && (isSuperAdmin(user) || (run.status !== "POSTED" && !run.postedAt && !(run.items || []).some(item => item.paidAt))) && (
                          <button type="button" onClick={() => deleteRun(run)} disabled={!!deletingRunId || !!postingRunId} className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-red-200 text-red-600 hover:bg-red-50 disabled:opacity-50" title="Delete payroll run" aria-label="Delete payroll run"><FiTrash2 /></button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {(selectedRun || isLoadingDetails) && (
        <PayrollRunDrawer
          run={selectedRun}
          isLoading={isLoadingDetails}
          onClose={() => setSelectedRun(null)}
          onMarkPaid={markPaid}
          accessToken={accessToken}
          onAdvanceRecoverySaved={() =>
            selectedRun?.id && openRun(selectedRun.id)
          }
        />
      )}
    </div>
  );
}

function PayrollRunDrawer({
  run,
  isLoading,
  onClose,
  onMarkPaid,
  accessToken,
  onAdvanceRecoverySaved,
}) {
  const [expandedItemId, setExpandedItemId] = useState(null);
  const [hrmsFilters, setHrmsFilters] = useState({});
  const filteredItems = useMemo(() => (run?.items || []).filter(item => matchesHrmsFilters(item,hrmsFilters)), [run,hrmsFilters]);

  const totals = useMemo(() => {
    const items = run?.items || [];

    return {
      gross: items.reduce(
        (sum, item) => sum + numberValue(item.grossEarnings),
        0,
      ),
      deductions: items.reduce(
        (sum, item) => sum + numberValue(item.totalDeductions),
        0,
      ),
      net: items.reduce((sum, item) => sum + numberValue(item.netPayable), 0),
      paid: items.filter((item) => item.status === "PAID").length,
    };
  }, [run]);

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/40 backdrop-blur-sm">
      <div className="h-full w-full max-w-6xl overflow-y-auto bg-[#f8fafc] shadow-2xl">
        <div className="sticky top-0 z-20 border-b border-gray-200 bg-white px-6 py-5 sm:px-8">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-sm text-gray-500">Payroll run details</p>

              <h2 className="mt-1 text-2xl font-bold text-gray-900">
                {run
                  ? `${formatMonth(run.periodYear, run.periodMonth)} Payroll`
                  : "Loading payroll run..."}
              </h2>

              {run ? (
                <div className="mt-2 flex flex-wrap items-center gap-2 text-sm text-gray-500">
                  <span>
                    {formatDate(run.periodStart)} – {formatDate(run.periodEnd)}
                  </span>

                  <span className="text-gray-300">•</span>

                  <span>
                    {run.runType === "OFF_CYCLE"
                      ? "Off-cycle settlement"
                      : "Regular payroll"}
                  </span>

                  <span
                    className={`rounded-full px-2.5 py-1 text-xs font-semibold ${statusStyle(
                      run.status,
                    )}`}
                  >
                    {run.status}
                  </span>
                </div>
              ) : null}
            </div>

            <button
              type="button"
              onClick={onClose}
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gray-100 text-gray-600 transition hover:bg-gray-200"
              aria-label="Close payroll details"
            >
              <FiX />
            </button>
          </div>
        </div>

        {isLoading || !run ? (
          <div className="py-24 text-center text-gray-500">
            Loading payroll details...
          </div>
        ) : (
          <div className="p-5 sm:p-8">
            <div className="grid grid-cols-2 gap-4 xl:grid-cols-5">
              <DrawerStat label="Employees" value={run.items?.length || 0} />
              <DrawerStat
                label="Gross earnings"
                value={formatMoney(totals.gross)}
              />
              <DrawerStat
                label="Deductions"
                value={formatMoney(totals.deductions)}
              />
              <DrawerStat label="Net payable" value={formatMoney(totals.net)} />
              <DrawerStat
                label="Employees paid"
                value={`${totals.paid} / ${run.items?.length || 0}`}
              />
            </div>

            {run.offCycleReason ? (
              <div className="mt-6 rounded-2xl border border-primary-100 bg-primary-50 p-5">
                <p className="text-xs font-bold uppercase tracking-wider text-primary-700">
                  Off-cycle settlement reason
                </p>
                <p className="mt-2 text-sm leading-6 text-primary-950">
                  {run.offCycleReason}
                </p>
              </div>
            ) : null}

            <div className="mt-8">
              <h3 className="text-lg font-bold text-gray-900">
                Employee payroll details
              </h3>

              <p className="mt-1 text-sm text-gray-500">
                Click an employee to review their salary, attendance, overtime,
                commissions, deductions, and daily calculations.
              </p>
            </div>

            <div className="mt-5"><HrmsFilters records={run.items || []} value={hrmsFilters} onChange={setHrmsFilters} /></div>
            <p className="mt-3 text-sm text-gray-500">Showing {filteredItems.length} of {run.items?.length || 0} employees. Totals above refer to the complete payroll run.</p>
            <div className="mt-5 space-y-4">
              {filteredItems.map((item) => {
                const isExpanded = expandedItemId === item.id;

                return (
                  <div
                    key={item.id}
                    className="overflow-hidden rounded-3xl border border-gray-200 bg-white shadow-sm"
                  >
                    <button
                      type="button"
                      onClick={() =>
                        setExpandedItemId((current) =>
                          current === item.id ? null : item.id,
                        )
                      }
                      className="flex w-full items-center gap-4 p-5 text-left transition hover:bg-gray-50 sm:p-6"
                    >
                      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-primary-50 font-bold text-primary-700">
                        {item.employeeNameSnapshot
                          ?.split(" ")
                          .map((part) => part[0])
                          .join("")
                          .slice(0, 2)
                          .toUpperCase()}
                      </div>

                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <h4 className="truncate font-bold text-gray-900">
                            {item.employeeNameSnapshot}
                          </h4>

                          <span
                            className={`rounded-full px-2.5 py-1 text-xs font-semibold ${statusStyle(
                              item.status,
                            )}`}
                          >
                            {item.status}
                          </span>
                        </div>

                        <p className="mt-1 text-sm text-gray-500">
                          {item.employeeCodeSnapshot} ·{" "}
                          {item.salaryTypeSnapshot === "DAILY"
                            ? "Daily salary"
                            : "Monthly salary"}
                        </p>
                      </div>

                      <div className="hidden text-right sm:block">
                        <p className="text-xs font-medium uppercase tracking-wide text-gray-400">
                          Net payable
                        </p>
                        <p className="mt-1 text-lg font-bold text-primary-700">
                          {formatMoney(item.netPayable)}
                        </p>
                      </div>

                      <div className="ml-1 text-gray-500">
                        {isExpanded ? <FiChevronUp /> : <FiChevronDown />}
                      </div>
                    </button>

                    <div className="flex items-center justify-between border-t border-gray-100 bg-gray-50 px-5 py-3 sm:hidden">
                      <span className="text-sm text-gray-500">Net payable</span>
                      <span className="font-bold text-primary-700">
                        {formatMoney(item.netPayable)}
                      </span>
                    </div>

                    {isExpanded ? (
                      <PayrollItemDetails
                        item={item}
                        run={run}
                        onMarkPaid={onMarkPaid}
                        accessToken={accessToken}
                        onAdvanceRecoverySaved={onAdvanceRecoverySaved}
                      />
                    ) : null}
                  </div>
                );
              })}

              {!run.items?.length ? (
                <div className="rounded-3xl border border-dashed border-gray-300 bg-white py-14 text-center text-gray-500">
                  This payroll run has no employee items.
                </div>
              ) : null}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function PayrollItemDetails({
  item,
  run,
  onMarkPaid,
  accessToken,
  onAdvanceRecoverySaved,
}) {
  const [showDailyRows, setShowDailyRows] = useState(false);

  const snapshot = item.calculationSnapshot || {};
  const days = Array.isArray(snapshot.dailyBreakdown)
    ? snapshot.dailyBreakdown
    : [];

  return (
    <div className="border-t border-gray-200 bg-slate-50 p-5 sm:p-6">
      <div className="grid grid-cols-1 gap-5 xl:grid-cols-3">
        <CalculationPanel
          icon={<FiClock />}
          title="Attendance and shift"
          rows={[
            [
              "Payroll period",
              `${formatDate(run.periodStart)} – ${formatDate(run.periodEnd)}`,
            ],
            [
              "Shift",
              snapshot.shiftStartTime && snapshot.shiftEndTime
                ? `${snapshot.shiftStartTime} – ${snapshot.shiftEndTime}`
                : "Not configured",
            ],
            [
              "Expected work/month",
              formatMinutes(
                numberValue(snapshot.expectedWorkingMinutes) *
                  numberValue(snapshot.eligibleDays),
              ),
            ],
            [
              "Total work/month",
              formatMinutes(
                (snapshot.dailyBreakdown || []).reduce(
                  (total, day) => total + numberValue(day.netWorkingMinutes),
                  0,
                ),
              ),
            ],
            ["Present days", item.presentDays],
            ["Late days", item.lateDays],
            ["Half days", item.halfDays],
            ["Absent-day equivalent", item.absentDays],
            ["Paid leave", item.paidLeaveDays],
            ["Unpaid leave", item.unpaidLeaveDays],
            [
              "Holiday credits settled",
              `${item.holidayAllowanceUsed} / ${item.monthlyHolidayAllowance}`,
            ],
          ]}
        />

        <CalculationPanel
          icon={<FiTrendingUp />}
          title="Earnings"
          accent="green"
          rows={[
            ["Basic salary snapshot", formatMoney(item.basicSalarySnapshot)],
            ["Daily salary rate", formatMoney(item.dailySalaryRate)],
            ["Base salary", formatMoney(item.baseSalaryAmount)],
            [`Unused holiday payout (${numberValue(snapshot.holidayEncashmentDays)} days)`, formatMoney(snapshot.holidayEncashmentAmount)],
            [
              `Overtime (${formatMinutes(item.overtimeMinutes)})`,
              formatMoney(item.overtimeAmount),
            ],
            ["Commission earned", formatMoney(item.commissionEarnedAmount)],
            [
              "Commission reversed",
              `-${formatMoney(item.commissionReversedAmount)}`,
            ],
            ["Net commission", formatMoney(item.netCommissionAmount)],
            ["Manual earnings", formatMoney(item.manualEarningAmount)],
            ["Gross earnings", formatMoney(item.grossEarnings)],
          ]}
        />

        <CalculationPanel
          icon={<FiTrendingDown />}
          title="Deductions and payout"
          accent="red"
          rows={[
            [
              `Absence (${item.unpaidAbsentDays} days)`,
              `-${formatMoney(item.absenceDeductionAmount)}`,
            ],
            [
              `Unpaid leave (${item.unpaidLeaveDays} days)`,
              `-${formatMoney(item.unpaidLeaveDeductionAmount)}`,
            ],
            [
              `Under-time (${formatMinutes(item.earlyExitMinutes)})`,
              `-${formatMoney(item.earlyExitDeductionAmount)}`,
            ],
            [
              "Manual deductions",
              `-${formatMoney(item.manualDeductionAmount)}`,
            ],
            ["Total deductions", `-${formatMoney(item.totalDeductions)}`],
            ["Final net payable", formatMoney(item.netPayable)],
            ["Payment mode", item.paymentModeSnapshot || "Not set"],
            ["Payment reference", item.paymentReference || "Not recorded"],
          ]}
        />
      </div>

      {run.status === "DRAFT" ? (
        <PayrollAdvanceRecoveryCard
          payrollItem={item}
          accessToken={accessToken}
          onSaved={onAdvanceRecoverySaved}
        />
      ) : null}

      {item.adjustments?.length ? (
        <div className="mt-5 overflow-hidden rounded-2xl border border-gray-200 bg-white">
          <div className="border-b border-gray-100 px-5 py-4">
            <h5 className="font-semibold text-gray-900">Manual adjustments</h5>
          </div>

          <div className="divide-y divide-gray-100">
            {item.adjustments.map((adjustment) => (
              <div
                key={adjustment.id}
                className="flex items-center justify-between gap-4 px-5 py-4 text-sm"
              >
                <div>
                  <p className="font-medium text-gray-900">
                    {String(adjustment.description || "").startsWith(
                      "EMPLOYEE_ADVANCE:",
                    )
                      ? "Employee advance recovery"
                      : adjustment.description}
                  </p>
                  <p className="mt-1 text-xs text-gray-500">
                    {formatDate(adjustment.createdAt)}
                  </p>
                </div>

                <span
                  className={
                    adjustment.type === "EARNING"
                      ? "font-bold text-emerald-700"
                      : "font-bold text-red-700"
                  }
                >
                  {adjustment.type === "EARNING" ? "+" : "-"}
                  {formatMoney(adjustment.amount)}
                </span>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      <div className="mt-5 overflow-hidden rounded-2xl border border-gray-200 bg-white">
        <button
          type="button"
          onClick={() => setShowDailyRows((current) => !current)}
          className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left transition hover:bg-gray-50"
        >
          <div>
            <h5 className="font-semibold text-gray-900">
              Daily attendance and payroll calculation
            </h5>
            <p className="mt-1 text-sm text-gray-500">
              Check-in/out, worked time, overtime, under-time, and day pay.
            </p>
          </div>

          {showDailyRows ? <FiChevronUp /> : <FiChevronDown />}
        </button>

        {showDailyRows ? (
          <div className="overflow-x-auto border-t border-gray-200">
            <table className="min-w-[1080px] w-full text-left text-sm">
              <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="px-5 py-4 font-semibold">Date</th>
                  <th className="px-5 py-4 font-semibold">Status</th>
                  <th className="px-5 py-4 font-semibold">Work sessions</th>
                  <th className="px-5 py-4 font-semibold">First / final</th>
                  <th className="px-5 py-4 font-semibold">Total worked</th>
                  <th className="px-5 py-4 font-semibold">Overtime</th>
                  <th className="px-5 py-4 font-semibold">Under-time</th>
                  <th className="px-5 py-4 text-right font-semibold">
                    Day pay
                  </th>
                </tr>
              </thead>

              <tbody className="divide-y divide-gray-100">
                {days.map((day) => (
                  <tr key={day.date} className="hover:bg-gray-50">
                    <td className="px-5 py-4 font-medium text-gray-900">
                      {formatDate(day.date)}
                    </td>

                    <td className="px-5 py-4">
                      <span
                        className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${attendanceStatusStyle(
                          day.status,
                        )}`}
                      >
                        {attendanceStatusLabel(day.status)}
                      </span>
                    </td>

                    <td className="px-5 py-4">
                      <SessionTimeline sessions={day.sessions} />
                    </td>

                    <td className="px-5 py-4 text-gray-700">
                      <div>{formatTime(day.checkInAt)}</div>
                      <div className="mt-1 text-xs text-gray-400">
                        Final: {formatTime(day.checkOutAt)}
                      </div>
                    </td>

                    <td className="px-5 py-4 font-medium text-gray-900">
                      {day.netWorkingMinutes === null
                        ? "—"
                        : formatMinutes(day.netWorkingMinutes)}
                    </td>

                    <td className="px-5 py-4 text-emerald-700">
                      {formatMinutes(day.overtimeMinutes)}
                    </td>

                    <td className="px-5 py-4 text-red-700">
                      {formatMinutes(
                        day.underTimeMinutes ?? day.earlyExitMinutes,
                      )}
                    </td>

                    <td className="px-5 py-4 text-right font-bold text-gray-900">
                      {formatMoney(day.attendancePayableAmount)}
                    </td>
                  </tr>
                ))}

                {!days.length ? (
                  <tr>
                    <td
                      colSpan={8}
                      className="px-5 py-10 text-center text-gray-500"
                    >
                      Daily calculation details are unavailable for older
                      payroll runs.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        ) : null}
      </div>

      <div className="mt-6 flex flex-col gap-3 border-t border-gray-200 pt-6 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-sm text-gray-500">Employee payout</p>
          <p className="mt-1 text-2xl font-bold text-primary-700">
            {formatMoney(item.netPayable)}
          </p>
        </div>

        {run.status === "POSTED" && item.status !== "PAID" ? (
          <button
            type="button"
            onClick={() => onMarkPaid(item.id)}
            className="rounded-xl bg-green-600 px-5 py-3 font-semibold text-white transition hover:bg-green-700"
          >
            Mark Employee Paid
          </button>
        ) : (
          <span
            className={`w-fit rounded-full px-4 py-2 text-sm font-semibold ${statusStyle(
              item.status,
            )}`}
          >
            {item.status === "PAID"
              ? `Paid${item.paidAt ? ` on ${formatDate(item.paidAt)}` : ""}`
              : "Draft payroll item"}
          </span>
        )}
      </div>
    </div>
  );
}

function CalculationPanel({ icon, title, rows, accent = "primary" }) {
  const styles = {
    primary: "border-gray-200 bg-white text-primary-700",
    green: "border-emerald-100 bg-white text-emerald-700",
    red: "border-red-100 bg-white text-red-700",
  };

  return (
    <div className={`rounded-3xl border p-5 ${styles[accent]}`}>
      <div className="flex items-center gap-3">
        <div className="rounded-xl bg-gray-50 p-2.5">{icon}</div>
        <h4 className="font-bold text-gray-900">{title}</h4>
      </div>

      <div className="mt-5 space-y-3">
        {rows.map(([label, value]) => (
          <div
            key={label}
            className="flex items-start justify-between gap-4 text-sm"
          >
            <span className="text-gray-500">{label}</span>
            <span className="text-right font-semibold text-gray-900">
              {value ?? "—"}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function StatCard({ title, value, icon, color }) {
  return (
    <div className="rounded-3xl border border-gray-100 bg-white p-6 shadow-sm">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500">{title}</p>
          <h2 className="mt-2 text-3xl font-bold text-gray-900">{value}</h2>
        </div>

        <div
          className={`flex h-12 w-12 items-center justify-center rounded-2xl ${color}`}
        >
          {icon}
        </div>
      </div>
    </div>
  );
}

function DrawerStat({ label, value }) {
  return (
    <div className="rounded-2xl bg-gray-50 p-5">
      <p className="text-sm text-gray-500">{label}</p>
      <p className="mt-2 text-lg font-bold text-gray-900">{value}</p>
    </div>
  );
}

function AmountCell({ value, strong = false }) {
  return (
    <td
      className={`px-5 py-4 text-right ${
        strong ? "font-bold text-primary-700" : "font-medium text-gray-900"
      }`}
    >
      {formatMoney(value)}
    </td>
  );
}

function TableHead({ children, align = "left" }) {
  return (
    <th
      className={`px-6 py-4 text-${align} text-xs font-semibold uppercase tracking-wider text-gray-500`}
    >
      {children}
    </th>
  );
}
